import React, { useState } from "react";
import "./Pagination.css";

type PageItem = number | "ellipsis";

// ページ番号の枠は常に5つ（「…」は数えない）。並び全体の枠の数も 7 つに固定し、ページを移っても幅が変わらないようにする
// （最初・最後のほうは「…」を2つ並べる。2026-11-02 ニスさん指示）
//   最初のほう: 1 2 3 4 … … 28 / まん中: 1 … 13 14 15 … 28 / 最後のほう: 1 … … 25 26 27 28 / 5ページ以下: 全部
const PAGE_SLOTS = 5;

function getPageItems(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= PAGE_SLOTS) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "ellipsis", "ellipsis", totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

// ページ送り（件数・‹ 番号 ›・ページ移動）。名刺の検索と閲覧履歴で共通
const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, pageSize, pageSizeOptions, onPageChange, onPageSizeChange }) => {
  const [goToPage, setGoToPage] = useState("");
  const pageItems = getPageItems(currentPage, totalPages);
  const parsedGoToPage = Number(goToPage);
  const canGoToPage = goToPage !== "" && Number.isInteger(parsedGoToPage) && parsedGoToPage >= 1 && parsedGoToPage <= totalPages;

  function handleGoToPage() {
    if (!canGoToPage) return;
    onPageChange(parsedGoToPage);
    setGoToPage("");
  }

  return (
    <div className="search-pagination" aria-label="Search result pagination">
      <label className="search-pagination-control">
        <select
          aria-label="表示件数"
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          className="search-pagination-select"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>{size}件</option>
          ))}
        </select>
      </label>
      <nav className="search-pagination-pages" aria-label="Result pages">
        <button
          type="button"
          className="search-pagination-page search-pagination-arrow"
          aria-label="前のページ"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <svg width="8" height="12" viewBox="0 0 8 12" aria-hidden="true"><path d="M7 1L2 6l5 5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
        </button>
        {pageItems.map((item, index) => item === "ellipsis" ? (
          <span key={`ellipsis-${index}`} className="search-pagination-ellipsis" aria-hidden="true">…</span>
        ) : (
          <button
            key={item}
            type="button"
            className={`search-pagination-page${item === currentPage ? " is-active" : ""}`}
            onClick={() => onPageChange(item)}
            aria-current={item === currentPage ? "page" : undefined}
          >
            {item}
          </button>
        ))}
        <button
          type="button"
          className="search-pagination-page search-pagination-arrow"
          aria-label="次のページ"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <svg width="8" height="12" viewBox="0 0 8 12" aria-hidden="true"><path d="M1 1l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
        </button>
      </nav>
      <div className="search-pagination-control">
        <label htmlFor="go-to-page" className="sr-only">移動するページ</label>
        <input
          id="go-to-page"
          type="number"
          min={1}
          max={totalPages}
          step={1}
          value={goToPage}
          onChange={(event) => setGoToPage(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleGoToPage();
          }}
          className="search-pagination-input"
        />
        <button
          type="button"
          className="search-pagination-go"
          disabled={!canGoToPage}
          onClick={handleGoToPage}
        >
          移動
        </button>
      </div>
    </div>
  );
};

export default Pagination;
